import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { parseISO, format } from 'date-fns';
import Handlebars from 'handlebars';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import DOMPurify from 'dompurify';
import { Icon } from '@iconify/react';

import { ChartsUnifiedProps } from '../../../../types';
import useFetchChartsData from '../../../../hooks/useFetchChartsData';
import useWindowSize from '../../../../hooks/useWindowsSize';
import config from '../../../../config';

import LoaderSpinner from '../../../LoaderSpinner/LoaderSpinner';
import noVarImg from '../../../../assets/charts_loading_bg.webp';
import { colorPalette } from '../../../../../src/colorConfig';

import './ChartsContent.css';

import rawEn from '../../../../locales/en.json';
import rawJa from '../../../../locales/ja.json';
import rawPt from '../../../../locales/pt.json';
import { Translation } from '../../../../types/translation';

const en: Translation = rawEn;
const ja: Translation = rawJa;
const pt: Translation = rawPt;

const translations: Record<string, Translation> = { en, ja, pt };
const lang = process.env.REACT_APP_LANGUAGE || 'en';
const t = translations[lang] || en;

Handlebars.registerHelper(
  'ifCond',
  function (this: any, v1: any, v2: any, options: any) {
    return v1 === v2 ? options.fn(this) : options.inverse(this);
  },
);

Handlebars.registerHelper(
  'getItem',
  function (data: any[], prop: string, value: string) {
    return data.find((itm) => itm[prop] === value);
  },
);

Handlebars.registerHelper('getValueItem', function (item: any, options: any) {
  const context = options.data.root;
  const measurementUnits = context.measurementUnits;
  if (measurementUnits !== 'IMP') {
    return item.value_sint !== -9999 ? item.value_sint.toString() : '-';
  } else {
    return item.value_simp !== -9999 ? item.value_simp.toString() : '-';
  }
});

Handlebars.registerHelper('getUnitItem', function (item: any, options: any) {
  const context = options.data.root;
  const measurementUnits = context.measurementUnits;
  return measurementUnits !== 'IMP'
    ? item.units_sint.toString()
    : item.units_simp.toString();
});

Handlebars.registerHelper('adjustWindDir', function (deg: string | number) {
  const d = parseFloat(deg as string);
  if (isNaN(d)) return 0;
  return (d + 180) % 360;
});

const MemoizedChart = memo(Chart);

const adjustWindDirection = (deg: number): number => (deg + 180) % 360;

const ensureISO8601 = (dateString: string): string => {
  let isoString = dateString.replace(' ', 'T');
  isoString = isoString.replace(' UTC', 'Z');
  return isoString;
};

const adjustTimestamp = (ts: string, utcOffset: number): number => {
  const date = parseISO(ensureISO8601(ts));
  const adjustedTime = date.getTime() + utcOffset * 60 * 60 * 1000;
  return adjustedTime;
};

const getArrowIndices = (
  timeSeries: string[],
  intervalInHours: number,
): number[] => {
  if (!timeSeries.length) return [];
  const arrowIndices: number[] = [];

  const baseDate = parseISO(ensureISO8601(timeSeries[0]));
  timeSeries.forEach((ts, i) => {
    const currentDate = parseISO(ensureISO8601(ts));
    if (isNaN(currentDate.getTime())) return;

    const diffMs = currentDate.getTime() - baseDate.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffHours % intervalInHours === 0) arrowIndices.push(i);
  });

  // Ensure the first and last indices are included
  if (!arrowIndices.includes(0)) arrowIndices.unshift(0);
  if (!arrowIndices.includes(timeSeries.length - 1)) {
    arrowIndices.push(timeSeries.length - 1);
  }
  return arrowIndices;
};

const yaxisLabelFormatter = (val: number | undefined): string => {
  if (val === undefined || val === null || isNaN(val)) return '';
  return val.toFixed(2);
};

const ChartsContent: React.FC<ChartsUnifiedProps> = ({
  dataBuoy,
  measurementUnits,
  lastTimeStamp,
  lastBuoyTimeStamp,
  selectedNameBuoy,
  logoBuoy,
  lat_str,
  longi_str,
  theme,
  utcOffset,
}) => {
  const [visibleCharts, setVisibleCharts] = useState<{
    [key: string]: boolean;
  }>({
    '40': true, // wave height
    '9': true, // wind speed
    '10': true, // wind direction
  });

  const [htmlDetailBuoy, setHtmlDetailBuoy] = useState<string>('');

  const toggleChartVisibility = useCallback((varName: string) => {
    setVisibleCharts((prev) => ({
      ...prev,
      [varName]: !prev[varName],
    }));
  }, []);

  const { apiUrl } = config;
  const windowSize = useWindowSize();
  const isMobile = windowSize.width !== undefined && windowSize.width <= 768;

  const fetchParams = useMemo(() => {
    const idBuoy = dataBuoy.length > 0 ? String(dataBuoy[0].id_buoy) : '8';
    const endTime = lastTimeStamp || new Date().toISOString();

    let endDate: Date;
    try {
      const sanitized = ensureISO8601(endTime);
      endDate = parseISO(sanitized);
      if (isNaN(endDate.getTime())) throw new Error('Invalid date');
    } catch {
      endDate = new Date();
    }
    const startDate = new Date(endDate.getTime() - 48 * 60 * 60 * 1000); // 48h ago
    return {
      id_buoy: idBuoy,
      id_vargen: ['40', '9', '10'],
      start_time: startDate.toISOString(),
      end_time: endDate.toISOString(),
    };
  }, [dataBuoy, lastTimeStamp]);

  const { data, loading, error } = useFetchChartsData(apiUrl, fetchParams);

  const processedData = useMemo(() => {
    if (!Array.isArray(data)) {
      console.error('Fetched data is not an array', data);
      return {
        timeSeries: [] as string[],
        variableData: {} as Record<string, { x: number; y: number }[]>,
        windDirs: [] as number[],
      };
    }
    const timestampsSet = new Set<string>();
    data.forEach((item) => {
      if (item.timestamp) timestampsSet.add(ensureISO8601(item.timestamp));
    });
    const timeSeries = Array.from(timestampsSet).sort((a, b) =>
      a.localeCompare(b),
    );

    const tsIndex: Record<string, number> = {};
    timeSeries.forEach((ts, i) => {
      tsIndex[ts] = i;
    });

    const variableData: Record<string, { x: number; y: number }[]> = {};
    const windDirs: number[] = new Array(timeSeries.length).fill(NaN);

    function parseTimestamp(ts: string) {
      return adjustTimestamp(ts, utcOffset);
    }

    data.forEach((item) => {
      if (!item.timestamp || !item.id_vargen) return;
      const isoTs = ensureISO8601(item.timestamp);
      const idx = tsIndex[isoTs];
      const localTime = parseTimestamp(isoTs);
      if (idx === undefined || isNaN(localTime)) return;

      const vargenId = String(item.id_vargen);
      const value =
        measurementUnits !== 'IMP' ? item.value_sint : item.value_simp;
      if (!variableData[vargenId]) variableData[vargenId] = [];
      variableData[vargenId].push({
        x: localTime,
        y: value !== null && value !== -9999 ? value : NaN,
      });
      if (vargenId === '10' && value !== null && value !== -9999) {
        windDirs[idx] = value;
      }
    });

    return { timeSeries, variableData, windDirs };
  }, [data, measurementUnits, utcOffset]);
  const variables = t.charts.variables;
  const checkboxLabels = t.charts.checkboxLabels;

  const variableColors: Record<string, string> = {};
  variables.forEach((v, i) => {
    variableColors[v.name] = colorPalette[i % colorPalette.length];
  });

  const hmaxSeries = useMemo(() => {
    const waveData = processedData.variableData['40'] || [];
    const waveDesc =
      variables.find((v) => v.name === '40')?.desc || 'Wave Height';
    return waveData.length
      ? [{ name: waveDesc, data: waveData, color: variableColors['40'] }]
      : [];
  }, [processedData.variableData, variables, variableColors]);

  const windSpeedSeries = useMemo(() => {
    const wData = processedData.variableData['9'] || [];
    const wDesc = variables.find((v) => v.name === '9')?.desc || 'Wind Speed';
    return wData.length
      ? [{ name: wDesc, data: wData, color: variableColors['9'] }]
      : [];
  }, [processedData.variableData, variables, variableColors]);

  const arrowIndices = useMemo(() => {
    if (!processedData.timeSeries.length) return [];
    const interval = isMobile ? 6 : 2;
    return getArrowIndices(processedData.timeSeries, interval);
  }, [processedData.timeSeries, isMobile]);

  const customHmaxTooltip = useCallback(
    (opts: any) => {
      const { series, seriesIndex, dataPointIndex } = opts;
      const val = series[seriesIndex][dataPointIndex];
      const timestamp = processedData.timeSeries[dataPointIndex];
      let dt: Date;
      try {
        dt = new Date(adjustTimestamp(timestamp, utcOffset));
      } catch {
        return '';
      }
      const formattedTime = format(dt, 'yyyy/MM/dd HH:mm');
      return `
        <div style="padding: 10px;">
          <div style="margin-bottom: 5px;">
            <strong>${t.charts.tooltip.time}:</strong> ${formattedTime}
          </div>
          <strong>${t.charts.tooltip.waveHeight}</strong>: ${val} m
        </div>
      `;
    },
    [processedData.timeSeries, utcOffset],
  );

  const customWindTooltip = useCallback(
    (opts: any) => {
      const { series, seriesIndex, dataPointIndex } = opts;
      const val = series[seriesIndex][dataPointIndex];
      const timestamp = processedData.timeSeries[dataPointIndex];
      let dt: Date;
      try {
        dt = new Date(adjustTimestamp(timestamp, utcOffset));
      } catch {
        return '';
      }
      const formattedTime = format(dt, 'HH:mm');
      const windDir = processedData.windDirs[dataPointIndex];
      const adjustedDir = adjustWindDirection(windDir);

      let windDirInfo = '';
      if (!isNaN(windDir)) {
        windDirInfo = `
          <div style="display: flex; align-items: center; margin-top: 5px;">
            <strong>${t.charts.tooltip.windDirection}:</strong>
            <div style="transform: rotate(${adjustedDir}deg); display: inline-block; margin-left: 10px;">
              <svg width="20" height="20">
                <line x1="10" y1="20" x2="10" y2="0" stroke="black" stroke-width="2" />
                <polygon points="5,5 10,0 15,5" fill="black" />
              </svg>
            </div>
            <span style="margin-left: 5px;">${windDir}°</span>
          </div>
        `;
      }

      return `
        <div style="padding: 10px; color: #000;">
          <div style="margin-bottom: 5px;">
            <strong>${t.charts.tooltip.time}:</strong> ${formattedTime}
          </div>
          <strong>${t.charts.tooltip.windSpeed}</strong>: ${val} m/s <br/>
          ${windDirInfo}
        </div>
      `;
    },
    [processedData.timeSeries, processedData.windDirs, utcOffset],
  );

  const commonChartOptions: ApexOptions = useMemo(() => {
    let minVal: number | undefined;
    let maxVal: number | undefined;
    const n = processedData.timeSeries.length;
    if (n > 0) {
      const first = adjustTimestamp(processedData.timeSeries[0], utcOffset);
      const last = adjustTimestamp(processedData.timeSeries[n - 1], utcOffset);
      minVal = first;
      maxVal = last;
    }

    return {
      chart: {
        toolbar: {
          show: true,
          tools: {
            download: false,
            selection: true,
            zoom: !isMobile,
            zoomin: !isMobile,
            zoomout: !isMobile,
            pan: !isMobile,
            reset: !isMobile,
          },
        },
        zoom: { enabled: !isMobile },
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800,
          animateGradually: {
            enabled: true,
            delay: 150,
          },
          dynamicAnimation: {
            enabled: true,
            speed: 350,
          },
        },
      },
      stroke: {
        curve: 'straight',
        width: 4,
      },
      fill: {
        type: 'solid',
        opacity: 0.8,
      },
      dataLabels: { enabled: false },
      markers: {
        size: 0,
        strokeColors: '#fff',
        strokeWidth: 2,
        hover: { size: 7 },
      },
      xaxis: {
        type: 'datetime',
        min: minVal,
        max: maxVal,
        labels: {
          datetimeUTC: false,
          rotate: -45,
          style: {
            fontSize: '12px',
            colors: theme === 'dark' ? '#FFF' : '#000',
          },
        },
        tickAmount: isMobile ? 6 : 10,
        axisTicks: {
          show: true,
          color: theme === 'dark' ? '#FFF' : '#78909C',
          width: 10,
        },
      },
      yaxis: {
        labels: {
          formatter: yaxisLabelFormatter,
          style: {
            fontSize: '12px',
            colors: theme === 'dark' ? '#FFF' : '#000',
          },
        },
        axisTicks: {
          show: true,
          color: theme === 'dark' ? '#FFF' : '#78909C',
          width: 10,
        },
        axisBorder: {
          show: true,
          color: theme === 'dark' ? '#FFF' : '#78909C',
        },
        title: {
          style: {
            fontSize: '16px',
            color: theme === 'dark' ? '#FFF' : '#000',
          },
        },
      },
      tooltip: {
        enabled: true,
        theme: 'light',
      },
      legend: {
        position: 'top',
        horizontalAlign: 'center',
        onItemClick: { toggleDataSeries: false },
        onItemHover: { highlightDataSeries: true },
      },
      responsive: [
        {
          breakpoint: 768,
          options: {
            stroke: { width: 3 },
            markers: { size: 0 },
          },
        },
      ],
    } as ApexOptions;
  }, [processedData.timeSeries, theme, isMobile, utcOffset]);

  const hmaxChartOptions: ApexOptions = useMemo(() => {
    const waveHeightLabel =
      variables.find((v) => v.name === '40')?.desc || 'Wave';
    return {
      ...commonChartOptions,
      tooltip: {
        ...commonChartOptions.tooltip,
        custom: customHmaxTooltip,
      },
      yaxis: {
        ...(Array.isArray(commonChartOptions.yaxis)
          ? commonChartOptions.yaxis[0]
          : commonChartOptions.yaxis),
        title: {
          text: `${waveHeightLabel} (m)`,
          style: {
            fontSize: '16px',
            color: theme === 'dark' ? '#FFF' : '#000',
          },
        },
      },
    };
  }, [commonChartOptions, customHmaxTooltip, variables, theme]);

  const windSpeedChartOptions: ApexOptions = useMemo(() => {
    const windSpeedLabel =
      variables.find((v) => v.name === '9')?.desc || 'Wind Speed';
    return {
      ...commonChartOptions,
      tooltip: {
        ...commonChartOptions.tooltip,
        custom: customWindTooltip,
      },
      yaxis: {
        ...(Array.isArray(commonChartOptions.yaxis)
          ? commonChartOptions.yaxis[0]
          : commonChartOptions.yaxis),
        title: {
          text: `${windSpeedLabel} (m/s)`,
          style: {
            fontSize: '16px',
            color: theme === 'dark' ? '#FFF' : '#000',
          },
        },
      },
    };
  }, [commonChartOptions, customWindTooltip, variables, theme]);

  const renderWindDirections = () => {
    return (
      <div className="wind-directions-section">
        <span
          className={`wind-direction-label-2 ${
            theme === 'dark' ? 'dark-theme' : ''
          }`}
        >
          <Icon
            icon="game-icons:windsock"
            className={`wind-direction-icon ${
              theme === 'dark' ? 'dark-theme' : ''
            }`}
          />
        </span>
        <div className="wind-directions-row force-margin">
          {arrowIndices.map((index) => {
            const dir = processedData.windDirs[index];
            const timestamp = processedData.timeSeries[index];
            if (isNaN(dir)) return null;

            let dt: Date;
            try {
              dt = new Date(adjustTimestamp(timestamp, utcOffset));
            } catch {
              return null;
            }
            const formattedTime = format(dt, 'HH:mm');
            const rotated = adjustWindDirection(dir);

            return (
              <div key={index} className="wind-direction">
                <div
                  className={`wind-direction-container ${
                    theme === 'dark' ? 'dark-theme' : ''
                  }`}
                >
                  <svg
                    width="26"
                    height="20"
                    style={{
                      transform: `rotate(${rotated}deg)`,
                      marginBottom: '8px',
                    }}
                  >
                    <line
                      x1="13"
                      y1="20"
                      x2="13"
                      y2="0"
                      stroke={theme === 'dark' ? 'white' : 'black'}
                      strokeWidth="2"
                    />
                    <polygon
                      points="9,6 13,0 17,6"
                      fill={theme === 'dark' ? 'white' : 'black'}
                    />
                  </svg>
                  <span
                    className={`arrow-timestamp ${
                      theme === 'dark' ? 'dark-theme' : ''
                    }`}
                  >
                    {formattedTime}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (
      !dataBuoy.length ||
      !dataBuoy[0].id_buoy ||
      !lastTimeStamp ||
      !lastBuoyTimeStamp
    ) {
      return;
    }
    const buoyId = dataBuoy[0].id_buoy;
    (async function fetchAndExtract() {
      try {
        const getTemplatePath = (id: number): string => {
          const buoysWithSpecificTemplate = [8, 25];
          return buoysWithSpecificTemplate.includes(id)
            ? `/assets/template/template_detail_buoy_id_${id}.hbs`
            : '/assets/template/template_detail_buoy.hbs';
        };

        const templatePath = getTemplatePath(buoyId);        const resp = await fetch(templatePath);
        if (!resp.ok)
          throw new Error(`Failed fetching template ${templatePath}`);
        const rawHbs = await resp.text();
        const tmpl = Handlebars.compile(rawHbs);

        const firstItem = dataBuoy[0];
        const context = {
          dataBuoy,
          firstItem,
          measurementUnits,
          lastTimeStamp,
          lastBuoyTimeStamp,
          selectedNameBuoy,
          logoBuoy,
          lat_str: firstItem.lat_str,
          longi_str: firstItem.longi_str,
        };

        const generatedHtml = tmpl(context);
        const parser = new DOMParser();
        const doc = parser.parseFromString(generatedHtml, 'text/html');
        const desiredEls = doc.querySelectorAll(
          '.section-brand, h1, .ubication, .date',
        );
        let extracted = '';
        desiredEls.forEach((el) => {
          if (el.classList.contains('date')) {
            const dateChildren = el.querySelectorAll(
              'span,div,p,h1,h2,h3,h4,h5,h6',
            );
            dateChildren.forEach((d) => {
              d.textContent = d.textContent || '';
            });
          }
          extracted += el.outerHTML;
        });

        const safe = DOMPurify.sanitize(extracted);
        setHtmlDetailBuoy((prev) => (prev !== safe ? safe : prev));
      } catch (err) {
        console.error('Error fetching Handlebars:', err);
        const sanitized = DOMPurify.sanitize(
          '<p>Error loading buoy details.</p>',
        );
        setHtmlDetailBuoy(sanitized);
      }
    })();
  }, [
    dataBuoy,
    lastTimeStamp,
    lastBuoyTimeStamp,
    measurementUnits,
    selectedNameBuoy,
    logoBuoy,
    lat_str,
    longi_str,
    utcOffset,
  ]);

  if (loading) {
    return <LoaderSpinner visible />;
  }
  if (error) {
    return <div>Error loading charts: {error}</div>;
  }

  return (
    <div className="charts-content">
      <div className="handlebars-and-variables">
        <div
          className="handlebars-content"
          dangerouslySetInnerHTML={{ __html: htmlDetailBuoy }}
        />
        <div className="variable-selection">
          {checkboxLabels
            .slice()
            .reverse()
            .map((cbox) => (
              <label key={cbox.vargen} className="variable-checkbox">
                <input
                  type="checkbox"
                  checked={visibleCharts[cbox.vargen]}
                  onChange={() => toggleChartVisibility(cbox.vargen)}
                  className="variable-checkbox-input"
                />
                {cbox.label}
              </label>
            ))}
        </div>
      </div>

      <div className="charts-wrapper">
        {visibleCharts['40'] && hmaxSeries.length > 0 && (
          <div className="chart-container chart-container-2">
            <MemoizedChart
              options={hmaxChartOptions}
              series={hmaxSeries}
              type="line"
              height="100%"
              width="100%"
            />
          </div>
        )}

        {visibleCharts['9'] && windSpeedSeries.length > 0 && (
          <>
            <div className="chart-container chart-container-2">
              <MemoizedChart
                options={windSpeedChartOptions}
                series={windSpeedSeries}
                type="line"
                height="100%"
                width="100%"
              />
            </div>
            {renderWindDirections()}
          </>
        )}

        {/* If neither wave height nor wind speed is visible => no variable selected */}
        {!visibleCharts['40'] && !visibleCharts['9'] && (
          <div className="no-var-container">
            <p className="no-var-message">
              {t.charts.messages.noVariableSelected}
            </p>
            <img
              src={noVarImg}
              alt="No Variable Selected"
              className="no-var-image"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ChartsContent;
